import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useCpdEnabled, useTenant } from '@/contexts/TenantContext';
import { useNavigate } from 'react-router-dom';
import { QuickStatsCard } from './QuickStatsCard';
import { RecentActivity } from './RecentActivity';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { 
  Activity, 
  Award, 
  TrendingUp, 
  Target, 
  Users, 
  Building2,
  CheckCircle2,
  BookOpen,
  MessageSquare,
  GraduationCap,
  Flame,
  Trophy,
  ArrowRight,
  Clock,
  Zap,
  Layers
} from 'lucide-react';
import { 
  fetchUserDashboardStats, 
  fetchAgents, 
  fetchRecentActivity, 
  fetchAdminDashboardStats,
  fetchCompanyDashboardStats,
  fetchUserProgressData
} from '@/lib/api';
import type { Agent } from '@/lib/api';
import { useOrganisation } from '@/contexts/OrganisationContext';
import { EnterpriseLearnerOverview } from './EnterpriseLearnerOverview';

export function Dashboard() {
  const { user, hasRole } = useAuth();
  const { tenant, isMasterTenant } = useTenant();
  const cpdEnabled = useCpdEnabled();
  const { hasEnterpriseHierarchy, effectiveRole, activeAssignment, activeUnit } = useOrganisation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<any>(null);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [recentActivities, setRecentActivities] = useState<any[]>([]);
  const [progressData, setProgressData] = useState<any>(null);

  useEffect(() => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    const loadDashboardData = async () => {
      try {
        setLoading(true);

        if (hasRole('admin')) {
          // Admin view - platform-wide data (filtered by tenant from URL, not user profile)
          const currentTenantId = tenant.tenant_id;
          const [adminStats, allAgents, platformActivity] = await Promise.all([
            fetchAdminDashboardStats(currentTenantId, { includeCpd: cpdEnabled }),
            fetchAgents({ status: 'active', tenant_id: currentTenantId }),
            fetchRecentActivity('admin', 10, undefined, currentTenantId)
          ]);
          
          setStats(adminStats);
          setAgents(allAgents);
          setRecentActivities(platformActivity);
          
        } else if (hasRole('company_admin')) {
          // Company admin view - company-specific data
          if (!user.company_id) {
            toast.error('Company information not found');
            setLoading(false);
            return;
          }

          const [companyStats, companyAgents, companyActivity] = await Promise.all([
            fetchCompanyDashboardStats(user.company_id),
            fetchAgents({ status: 'active', company_id: user.company_id }),
            fetchRecentActivity('company', 10, user.company_id)
          ]);
          
          setStats(companyStats);
          setAgents(companyAgents);
          setRecentActivities(companyActivity);
          
        } else {
          // Regular user view - personal performance data
          const [userStats, userProgress, userActivity] = await Promise.all([
            fetchUserDashboardStats(user.id),
            fetchUserProgressData(user.id, { includeCpd: cpdEnabled }),
            fetchRecentActivity('user', 10)
          ]);
          
          setStats(userStats);
          setProgressData(userProgress);
          setRecentActivities(userActivity);
        }
        
      } catch (error) {
        console.error('Failed to load dashboard:', error);
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [user, hasRole, tenant.tenant_id, cpdEnabled]);

  const getRoleSubtitle = () => {
    const roleText = (() => {
      if (hasRole('admin')) {
        return 'Platform Administrator';
      } else if (hasRole('company_admin')) {
        return 'Company Administrator';
      } else if (hasRole('sales_rep')) {
        return 'Sales Representative';
      }
      return 'Team Member';
    })();
    
    // For Platform Administrators: show tenant name
    // For Company Admins/Users: show company name
    if (hasRole('admin') && tenant?.tenant_name) {
      return `${roleText} at ${tenant.tenant_name}`;
    } else if (user?.company_name) {
      return `${roleText} at ${user.company_name}`;
    }
    return roleText;
  };

  const renderQuickStats = () => {
    if (!stats) return null;

    if (hasRole('admin')) {
      // For super admin (master tenant), show 5 cards including Total Tenants
      // For white-label tenant admins, show 4 cards
      return (
        <>
          {isMasterTenant && (
            <QuickStatsCard
              icon={Layers}
              title="Total Tenants"
              value={stats.totalTenants?.toString() || '0'}
              subtitle="White-label partners"
            />
          )}
          <QuickStatsCard
            icon={Users}
            title="Total Users"
            value={stats.totalUsers?.toString() || '0'}
            subtitle="Platform-wide"
          />
          <QuickStatsCard
            icon={Building2}
            title="Total Companies"
            value={stats.totalCompanies?.toString() || '0'}
            subtitle="Active organizations"
          />
          <QuickStatsCard
            icon={Activity}
            title="Simulations Completed"
            value={stats.totalSimulations?.toString() || '0'}
            subtitle="All time"
          />
          <QuickStatsCard
            icon={Target}
            title="Average Score"
            value={stats.averageScore == null ? '—' : `${stats.averageScore}%`}
            subtitle="Platform-wide"
          />
        </>
      );
    }

    if (hasRole('company_admin')) {
      return (
        <>
          <QuickStatsCard
            icon={Users}
            title="Team Members"
            value={stats.teamMembers?.toString() || '0'}
            subtitle="Active users"
          />
          <QuickStatsCard
            icon={Activity}
            title="Simulations Completed"
            value={stats.totalSimulations?.toString() || '0'}
            subtitle="By your team"
          />
          <QuickStatsCard
            icon={Target}
            title="Team Average Score"
            value={stats.teamAverageScore == null ? '—' : `${stats.teamAverageScore}%`}
            subtitle="Overall performance"
          />
          <QuickStatsCard
            icon={CheckCircle2}
            title="Available Agents"
            value={stats.availableAgents?.toString() || '0'}
            subtitle="For your team"
          />
        </>
      );
    }

    // Default for regular users - show comprehensive stats from progressData
    const simCount = progressData?.totalSimulations || stats?.totalSimulations || 0;
    const coachingCount = progressData?.totalCoaching || 0;
    const avgScore = progressData ? progressData.avgScore : stats?.averageScore ?? null;
    
    return (
      <>
        <QuickStatsCard
          icon={Activity}
          title="Simulations"
          value={simCount.toString()}
          subtitle={progressData?.simTrend || 'Completed'}
        />
        <QuickStatsCard
          icon={MessageSquare}
          title="Coaching Sessions"
          value={coachingCount.toString()}
          subtitle={progressData?.coachingTrend || 'Completed'}
        />
        <QuickStatsCard
          icon={Target}
          title="Average Score"
          value={avgScore == null ? '—' : `${avgScore}%`}
          subtitle={progressData?.avgTrend || 'Overall'}
          progress={avgScore ?? undefined}
        />
        {cpdEnabled ? (
          <QuickStatsCard
            icon={Award}
            title="CPD Points"
            value={(progressData?.cpdPoints || 0).toString()}
            subtitle={progressData?.cpdTrend || 'Earned'}
          />
        ) : null}
      </>
    );
  };

  // Render user-specific performance dashboard
  const renderUserPerformanceDashboard = () => {
    if (!progressData) return null;

    const currentStreak = progressData.currentStreak || 0;
    const badges = progressData.badges || [];
    const skills = progressData.skills || [];

    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Performance Overview Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              Performance Overview
            </CardTitle>
            <CardDescription>Your progress and achievements</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <div className="text-3xl font-bold text-primary">
                  {progressData.bestScore || stats?.bestScore || 0}%
                </div>
                <p className="text-sm text-muted-foreground">Best Score</p>
              </div>
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <div className="text-3xl font-bold text-foreground flex items-center justify-center gap-1">
                  <Flame className="h-6 w-6 text-orange-500" />
                  {currentStreak}
                </div>
                <p className="text-sm text-muted-foreground">Day Streak</p>
              </div>
            </div>
            
            {/* Skills Breakdown */}
            {skills.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium text-sm">Top Skills</h4>
                {skills.slice(0, 3).map((skill: any) => (
                  <div key={skill.name} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{skill.name}</span>
                      <span className="font-medium">{skill.avgScore}%</span>
                    </div>
                    <Progress value={skill.avgScore} className="h-2" />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-primary" />
              Quick Actions
            </CardTitle>
            <CardDescription>Jump back in</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button 
              variant="outline" 
              className="w-full justify-between h-auto py-3"
              onClick={() => navigate('/simulations')}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Target className="h-5 w-5 text-primary" />
                </div>
                <div className="text-left">
                  <div className="font-medium">Start Simulation</div>
                  <div className="text-xs text-muted-foreground">Practice with AI agents</div>
                </div>
              </div>
              <ArrowRight className="h-4 w-4" />
            </Button>
            
            <Button 
              variant="outline" 
              className="w-full justify-between h-auto py-3"
              onClick={() => navigate('/coaching')}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <MessageSquare className="h-5 w-5 text-primary" />
                </div>
                <div className="text-left">
                  <div className="font-medium">Coaching Session</div>
                  <div className="text-xs text-muted-foreground">AI-guided preparation</div>
                </div>
              </div>
              <ArrowRight className="h-4 w-4" />
            </Button>
            
            <Button 
              variant="outline" 
              className="w-full justify-between h-auto py-3"
              onClick={() => navigate('/pathways')}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <GraduationCap className="h-5 w-5 text-primary" />
                </div>
                <div className="text-left">
                  <div className="font-medium">Learning Pathways</div>
                  <div className="text-xs text-muted-foreground">Continue structured learning</div>
                </div>
              </div>
              <ArrowRight className="h-4 w-4" />
            </Button>
          </CardContent>
        </Card>

        {/* Badges Card */}
        {badges.length > 0 && (
          <Card className="lg:col-span-2">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Trophy className="h-5 w-5 text-primary" />
                    Recent Achievements
                  </CardTitle>
                  <CardDescription>Badges you've earned</CardDescription>
                </div>
                <Button variant="ghost" size="sm" onClick={() => navigate('/progress')}>
                  View All <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4">
                {badges.slice(0, 6).map((badge: any) => (
                  <div 
                    key={badge.id} 
                    className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg border"
                  >
                    <div className="text-2xl">{badge.icon || '🏆'}</div>
                    <div>
                      <div className="font-medium text-sm">{badge.name}</div>
                      <div className="text-xs text-muted-foreground">{badge.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  const getSimulationsTitle = () => {
    return hasRole('admin') ? 'All Platform Simulations' : 'Available Simulations';
  };

  const getActivityTitle = () => {
    return hasRole('admin') || hasRole('company_admin')
      ? 'Recent Platform Activity'
      : 'Recent Sessions';
  };

  const showUserInfoInActivity = hasRole('admin') || hasRole('company_admin');
  const isRegularUser = !hasRole('admin') && !hasRole('company_admin');
  const isEnterpriseLearner = hasEnterpriseHierarchy && effectiveRole === 'user';

  if (loading) {
    return (
      <div className="p-8 space-y-8">
        <div className="mb-8">
          <Skeleton className="h-10 w-64 mb-2" />
          <Skeleton className="h-6 w-96" />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <Card key={i}>
              <CardContent className="p-6">
                <Skeleton className="h-24 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <Card key={i}>
              <CardContent className="p-6">
                <Skeleton className="h-48 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8">
      {/* Welcome Section */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">
          Welcome back, {user?.name || 'User'}
        </h1>
        <p className="text-muted-foreground">
          {isEnterpriseLearner
            ? `${activeUnit?.name || activeAssignment?.unit_name || 'Your unit'} • Track your progress and continue learning`
            : `${getRoleSubtitle()} • ${isRegularUser ? 'Track your progress and continue learning' : 'Ready to continue your training?'}`}
        </p>
      </div>

      {isEnterpriseLearner && <EnterpriseLearnerOverview />}

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {renderQuickStats()}
      </div>

      {/* User Performance Dashboard - Only for regular users */}
      {isRegularUser && renderUserPerformanceDashboard()}

      {/* Admin/Company Admin Dashboard - Statistics View */}
      {!isRegularUser && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Quick Actions for Admin/Company Admin */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-primary" />
                Quick Actions
              </CardTitle>
              <CardDescription>Manage your platform</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button 
                variant="outline" 
                className="w-full justify-between h-auto py-3"
                onClick={() => navigate('/admin/agents')}
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Target className="h-5 w-5 text-primary" />
                  </div>
                  <div className="text-left">
                    <div className="font-medium">Manage Agents</div>
                    <div className="text-xs text-muted-foreground">{agents.length} active simulations</div>
                  </div>
                </div>
                <ArrowRight className="h-4 w-4" />
              </Button>
              
              <Button 
                variant="outline" 
                className="w-full justify-between h-auto py-3"
                onClick={() => navigate('/admin/users')}
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Users className="h-5 w-5 text-primary" />
                  </div>
                  <div className="text-left">
                    <div className="font-medium">View Users</div>
                    <div className="text-xs text-muted-foreground">{stats?.totalUsers || stats?.teamMembers || 0} total users</div>
                  </div>
                </div>
                <ArrowRight className="h-4 w-4" />
              </Button>
              
              {hasRole('admin') && (
                <Button 
                  variant="outline" 
                  className="w-full justify-between h-auto py-3"
                  onClick={() => navigate('/admin/companies')}
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Building2 className="h-5 w-5 text-primary" />
                    </div>
                    <div className="text-left">
                      <div className="font-medium">Companies</div>
                      <div className="text-xs text-muted-foreground">{stats?.totalCompanies || 0} organizations</div>
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4" />
                </Button>
              )}
              
              <Button 
                variant="outline" 
                className="w-full justify-between h-auto py-3"
                onClick={() => navigate('/leaderboards')}
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Trophy className="h-5 w-5 text-primary" />
                  </div>
                  <div className="text-left">
                    <div className="font-medium">Leaderboards</div>
                    <div className="text-xs text-muted-foreground">View top performers</div>
                  </div>
                </div>
                <ArrowRight className="h-4 w-4" />
              </Button>
            </CardContent>
          </Card>

          {/* Usage This Month - For Company Admin */}
          {hasRole('company_admin') && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-primary" />
                  Usage This Month
                </CardTitle>
                <CardDescription>Team activity overview</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <div className="text-3xl font-bold text-primary">
                      {stats?.totalSimulations || 0}
                    </div>
                    <p className="text-sm text-muted-foreground">Sessions</p>
                  </div>
                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <div className="text-3xl font-bold text-foreground">
                      {stats?.teamAverageScore == null ? '—' : `${stats?.teamAverageScore}%`}
                    </div>
                    <p className="text-sm text-muted-foreground">Avg Score</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Active Team Members</span>
                    <span className="font-medium">{stats?.teamMembers || 0}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Available Agents</span>
                    <span className="font-medium">{stats?.availableAgents || 0}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Platform Health - For Admin */}
          {hasRole('admin') && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-primary" />
                  Platform Overview
                </CardTitle>
                <CardDescription>System-wide metrics</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <div className="text-3xl font-bold text-primary">
                      {stats?.totalSimulations || 0}
                    </div>
                    <p className="text-sm text-muted-foreground">Total Sessions</p>
                  </div>
                  <div className="text-center p-4 bg-muted/50 rounded-lg">
                    <div className="text-3xl font-bold text-foreground">
                      {stats?.averageScore == null ? '—' : `${stats?.averageScore}%`}
                    </div>
                    <p className="text-sm text-muted-foreground">Avg Score</p>
                  </div>
                </div>
                <div className="space-y-2">
                  {isMasterTenant && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Total Tenants</span>
                      <span className="font-medium">{stats?.totalTenants || 0}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Active Agents</span>
                    <span className="font-medium">{agents.length}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Total Companies</span>
                    <span className="font-medium">{stats?.totalCompanies || 0}</span>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full mt-2"
                  onClick={() => navigate('/admin')}
                >
                  View Full Dashboard <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Recent Activity */}
      <RecentActivity
        title={getActivityTitle()}
        activities={recentActivities}
        showUserInfo={showUserInfoInActivity}
      />
    </div>
  );
}
